// src/components/lists/base/ListBase.tsx
// Base component with shared logic for all list variants

import { useState } from 'react';
import { ListState, ListUtils } from './ListTypes';
import { fallbackCopyToClipboard } from './listUtils';

// Custom hook for shared state management
export const useListState = (): ListState => {
  const [openItemId, setOpenItemId] = useState<number | null>(null);
  return { openItemId, setOpenItemId };
};

// Custom hook for shared utility functions
export const useListUtils = (
  openItemId: number | null,
  setOpenItemId: (id: number | null) => void
): ListUtils => {
  const handleRowClick = (id: number) => {
    if (openItemId !== id) {
      setOpenItemId(id);
    }
  };

  const handleNameClick = (id: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (openItemId === id) {
      setOpenItemId(null);
    }
  };

  const handleCopy = async (
    text: string,
    event: React.MouseEvent,
    onSuccess?: (position: { x: number; y: number }) => void
  ): Promise<void> => {
    event.stopPropagation();

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        const button = event.currentTarget as HTMLElement;
        const rect = button.getBoundingClientRect();
        if (onSuccess) {
          onSuccess({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          });
        }
      } else {
        const success = fallbackCopyToClipboard(text);
        if (success && onSuccess) {
          const button = event.currentTarget as HTMLElement;
          const rect = button.getBoundingClientRect();
          onSuccess({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          });
        }
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getItemId = (item: any): number => item.id;
  const renderName = (item: any): string =>
    item.name || `${item.firstName} ${item.lastName}` || 'Unknown';

  return {
    handleRowClick,
    handleNameClick,
    handleCopy,
    getItemId,
    renderName,
  };
};

export const createKeyDownHandler = (
  id: number,
  onOpen: (id: number) => void,
  onClose: () => void
) => {
  return (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(id);
    }
    if (event.key === 'Escape') {
      onClose();
    }
  };
};

export const getAccessibilityProps = (id: number, isOpen: boolean) => {
  return {
    role: 'button',
    tabIndex: 0,
    'aria-expanded': isOpen,
    'aria-controls': `list-item-details-${id}`,
    id: `list-item-${id}`,
  };
};
